import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  AccordionChevronIcon,
  CalendarIcon,
} from "../NewOrderDailyLimits/icons";
import { ChevronDownIcon } from "../NewOrderSelectApp/icons";
import useIsPhone from "../../../hooks/useIsPhone";
import { NewOrderDateRangeCardProps } from "./types";

const StepToggle: React.FC<{
  onTitleToggle?: () => void;
  collapsed?: boolean;
}> = ({ onTitleToggle, collapsed }) =>
  onTitleToggle ? (
    <button
      type="button"
      className={cs(styles.stepToggle, styles.stepToggleButton, {
        [styles.stepToggleCollapsed]: collapsed,
      })}
      onClick={onTitleToggle}
      aria-label={collapsed ? "Expand step" : "Collapse step"}
    >
      <AccordionChevronIcon className={styles.accordionChevron} />
    </button>
  ) : (
    <span
      className={cs(styles.stepToggle, {
        [styles.stepToggleCollapsed]: collapsed,
      })}
      aria-hidden
    >
      <AccordionChevronIcon className={styles.accordionChevron} />
    </span>
  );

/**
 * "1. Date range" step card of the new-order flow. Expanded (Figma 1035:8382):
 * chevron + title, Start/End date pickers with time selects, timezone caption,
 * a Days counter and Done pinned to the bottom-right corner. Collapsed
 * (725:91157): one line with a "From … Till …" summary and the day count.
 * Pure presentational — real Calendar / TimeSelect components come in via the
 * *Slot props; without them it renders dumb display triggers.
 */
export const NewOrderDateRangeCard: React.FC<NewOrderDateRangeCardProps> = ({
  collapsed = false,
  stepTitle = "1. Date range",
  onTitleToggle,
  startDateText,
  endDateText,
  startTimeText,
  endTimeText,
  startDateSlot,
  startTimeSlot,
  endDateSlot,
  endTimeSlot,
  timezoneLabel,
  days = 0,
  daysInvalid = false,
  error = false,
  doneDisabled = true,
  onDone,
  className,
}) => {
  const isPhone = useIsPhone();

  if (collapsed) {
    return (
      <div className={cs(styles.card, styles.cardCollapsed, className)}>
        <div className={styles.collapsedTitleCol}>
          <StepToggle onTitleToggle={onTitleToggle} collapsed />
          <h3 className={styles.stepTitle}>{stepTitle}</h3>
        </div>

        <div className={styles.collapsedSummary}>
          <span className={styles.collapsedGroup}>
            <span className={styles.collapsedCaption}>From</span>
            <span className={styles.collapsedValue}>{startDateText}</span>
          </span>
          <span className={styles.collapsedGroup}>
            <span className={styles.collapsedCaption}>Till</span>
            <span className={styles.collapsedValue}>{endDateText}</span>
          </span>
        </div>

        <span className={styles.collapsedDays}>{days} days</span>
      </div>
    );
  }

  const dateTrigger = (text?: string) => (
    <button type="button" className={styles.dateTrigger}>
      <span>{text}</span>
      <CalendarIcon className={styles.icon20} />
    </button>
  );

  const timeTrigger = (text?: string) => (
    <button type="button" className={styles.timeTrigger}>
      <span>{text}</span>
      <ChevronDownIcon className={styles.icon20} />
    </button>
  );

  // Desktop: after the end time. Phone: beside the day count, so the end
  // date row keeps the start row's widths.
  const timezone = timezoneLabel && (
    <span className={styles.timezone}>{timezoneLabel}</span>
  );

  return (
    <div
      className={cs(styles.card, { [styles.cardError]: error }, className)}
    >
      <div className={styles.startCol}>
        <div className={styles.accordionTitle}>
          <StepToggle onTitleToggle={onTitleToggle} />
          <h3 className={styles.stepTitle}>{stepTitle}</h3>
        </div>
      </div>

      <div className={styles.fields}>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Start date</span>
          <div className={styles.fieldControls}>
            <div className={styles.dateCell}>
              {startDateSlot ?? dateTrigger(startDateText)}
            </div>
            <div className={styles.timeCell}>
              {startTimeSlot ?? timeTrigger(startTimeText)}
            </div>
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>End date</span>
          <div className={styles.fieldControls}>
            <div className={styles.dateCell}>
              {endDateSlot ?? dateTrigger(endDateText)}
            </div>
            <div className={styles.timeCell}>
              {endTimeSlot ?? timeTrigger(endTimeText)}
            </div>
            {!isPhone && timezone}
          </div>
        </div>
      </div>

      <div className={styles.daysCol}>
        <span className={styles.daysLabel}>Days:</span>
        <span
          className={cs(styles.daysValue, {
            [styles.daysInvalid]: daysInvalid,
          })}
        >
          {days}
        </span>
        {isPhone && timezone}
      </div>

      <div className={styles.doneCorner}>
        <button
          type="button"
          className={styles.doneButton}
          disabled={doneDisabled}
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default NewOrderDateRangeCard;
